import React, { useEffect, useMemo, useState } from 'react';
import { SelectorConfirmacion } from './FechaTentativa';
import { toast } from 'react-hot-toast';
import { LuChevronLeft, LuChevronRight, LuX, LuMapPin, LuPlus, LuSearch, LuCheck } from 'react-icons/lu';
import api from '../../services/api';
import UbicacionEnMapa, { guardarUbicacionManual } from '../mapa/UbicacionEnMapa';
import { formatDateISO, getTodayISO } from '../../utils/dateUtils';
import AgendaHuecos from './AgendaHuecos';
import AvatarTecnico from '../ui/AvatarTecnico';
import AvisoHoraPasada from './AvisoHoraPasada';
import HoraRango from './HoraRango';
import { horaTexto } from '../../utils/ordenes';

// Nueva visita (3-oct-2026, opción B "paso a paso" + el "elegí el hueco" de la C).
//   1 · ¿A quién?  — buscar cliente / N° de serie, recientes, o cliente nuevo
//   2 · ¿Qué?      — motivo; si tiene tarifa mensual, equipos de UNA dirección
//   3 · ¿Quién y cuándo? — la agenda del día por técnico: se toca el hueco libre
// Clientes con tarifa (MODO AGUA): al cerrar la visita el técnico carga cada equipo
// por N/S y va al cierre mensual (ver CargaPorSerieSheet). Editar sigue en OrdenForm.

const MOTIVOS = ['Service', 'Revisar falla', 'Instalación', 'Retiro', 'Otro'];
const INPUT = 'w-full h-12 px-3.5 rounded-xl bg-chip text-ink text-body font-medium outline-none focus:ring-2 focus:ring-[#D13A28]/40 placeholder:text-muted';
const chipCls = (on) => `h-10 px-3.5 rounded-full inline-flex items-center gap-2 text-label font-bold border-2 transition-all active:scale-95 ${on ? 'border-brand-red bg-[rgba(232,66,47,0.10)] text-ink' : 'border-transparent bg-chip text-secondary'}`;
const iniciales = (n) => (n || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
const sumarDias = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return formatDateISO(d); };

function Check({ on }) {
    return (
        <span className={`w-6 h-6 rounded-lg shrink-0 flex items-center justify-center ${on ? 'bg-brand-red text-white' : 'border-2 border-[#A8A29E]/60'}`}>
            {on && <LuCheck size={14} strokeWidth={3} />}
        </span>
    );
}

export default function VisitaForm({ tecnicos = [], onGuardado, onCancelar, onPedidoEmpresa = null }) {
    const [paso, setPaso] = useState(1);
    // Paso 1
    const [clientes, setClientes] = useState([]);
    const [ordenes, setOrdenes] = useState([]);
    const [q, setQ] = useState('');
    const [buscandoNS, setBuscandoNS] = useState(false);
    const [cliente, setCliente] = useState(null);
    const [nuevo, setNuevo] = useState(null);
    // Mapa en vivo (13-oct-2026): igual que al crear un cliente desde Clientes, para que quede bien ubicado
    const [puntoNuevo, setPuntoNuevo] = useState(null);
    const [puntoSede, setPuntoSede] = useState(null);           // { nombre, telefono, direccion, localidad }
    // Paso 2
    const [motivo, setMotivo] = useState('Service');
    const [otroTexto, setOtroTexto] = useState('');   // 1-nov-2026: con "Otro", qué es (sale como título)
    const [nota, setNota] = useState('');
    const [verNota, setVerNota] = useState(false);
    const [sedes, setSedes] = useState([]);
    const [sedeId, setSedeId] = useState(null);
    const [abierta, setAbierta] = useState(null);       // sede desplegada en la lista
    const [series, setSeries] = useState([]);
    const [buscaEq, setBuscaEq] = useState('');
    const [nuevaSede, setNuevaSede] = useState(null);
    const [preSerie, setPreSerie] = useState(null);     // N/S que vino del buscador del paso 1
    // Paso 3
    const [fecha, setFecha] = useState(getTodayISO());
    const [hueco, setHueco] = useState(null);           // { tecnicoId, franja }
    const [hora, setHora] = useState('');
    const [tentativa, setTentativa] = useState(false);
    const [verHora, setVerHora] = useState(false);
    // 1-nov-2026: cuánto cobra el técnico en el lugar (opcional) y cómo; antes no se cargaba y el técnico no sabía
    const [monto, setMonto] = useState('');
    const [pago, setPago] = useState('EFECTIVO');
    const [guardando, setGuardando] = useState(false);

    const hoy = getTodayISO();

    useEffect(() => {
        api.get('/clientes', { params: { size: 1000 } }).then(r => setClientes(r.data?.content || r.data || [])).catch(() => {});
        api.get('/ordenes', { params: { desde: sumarDias(hoy, -60), hasta: sumarDias(hoy, 120) } })
            .then(r => setOrdenes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, [hoy]);

    const conTarifa = !!cliente?.tieneTarifa;

    // ── Paso 1: recientes y búsqueda ─────────────────────────────────────────
    const recientes = useMemo(() => {
        const vistos = new Set(); const out = [];
        [...ordenes].sort((a, b) => String(b.fechaProgramada).localeCompare(String(a.fechaProgramada))).forEach(o => {
            if (o.clienteId && !vistos.has(o.clienteId)) {
                const c = clientes.find(x => x.id === o.clienteId);
                if (c) { vistos.add(o.clienteId); out.push(c); }
            }
        });
        // 1-nov-2026: las empresas con cobro mensual van por "Pedido de empresa", no por acá
        return out.filter(c => !(onPedidoEmpresa && c.tieneTarifa)).slice(0, 5);
    }, [ordenes, clientes, onPedidoEmpresa]);
    const resultados = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (t.length < 2) return [];
        return clientes.filter(c => [c.nombre, c.direccion, c.telefono].filter(Boolean).join(' ').toLowerCase().includes(t)).slice(0, 8);
    }, [q, clientes]);

    const elegirCliente = (c, serie = null) => {
        if (onPedidoEmpresa && c?.tieneTarifa) {
            toast(`${c.nombre} se cobra por mes: va por Pedido de empresa`);
            onPedidoEmpresa({ id: c.id, nombre: c.nombre });
            return;
        }
        setCliente(c); setNuevo(null); setPreSerie(serie);
        setSedes([]); setSedeId(null); setSeries([]); setAbierta(null);
        setPaso(2);
    };
    const buscarSerie = async () => {
        const s = q.trim().toUpperCase();
        if (s.length < 3) return;
        setBuscandoNS(true);
        try {
            const r = await api.get('/equipos/historial', { params: { serie: s } });
            const eq = r.data?.equipo;
            if (!r.data?.encontrado || !eq) {
                const sug = r.data?.sugerencias || [];
                toast(sug.length ? `No hay uno exacto. Parecidos: ${sug.slice(0, 4).join(', ')}` : 'No encontré ese N° de serie');
                return;
            }
            const c = clientes.find(x => x.id === eq.clienteId) || { id: eq.clienteId, nombre: eq.cliente };
            elegirCliente(c, { serie: eq.serie, sedeId: eq.sedeId });
        } catch { toast.error('No se pudo buscar'); } finally { setBuscandoNS(false); }
    };

    // ── Paso 2: direcciones y equipos ────────────────────────────────────────
    useEffect(() => {
        if (!cliente?.id) return;
        api.get(`/sedes/cliente/${cliente.id}`).then(r => {
            const l = Array.isArray(r.data) ? r.data : [];
            setSedes(l);
            if (preSerie?.sedeId) {
                setSedeId(preSerie.sedeId); setAbierta(preSerie.sedeId);
                if (cliente.tieneTarifa) setSeries([preSerie.serie]);
            } else if (l.length === 1) { setSedeId(l[0].id); setAbierta(l[0].id); }
        }).catch(() => {});
    }, [cliente, preSerie]);

    const sede = sedes.find(s => s.id === sedeId) || null;
    const sedesFiltradas = useMemo(() => {
        const t = buscaEq.trim().toLowerCase();
        if (!t) return sedes;
        return sedes.filter(s => [s.nombreSede, s.direccion, ...(s.equipos || []).map(e => e.numeroSerie)].filter(Boolean).join(' ').toLowerCase().includes(t));
    }, [sedes, buscaEq]);

    const toggleEquipo = (s, ns) => {
        if (sedeId && sedeId !== s.id && series.length) {
            toast.error('Una visita es a un solo lugar. Para el otro, armá otra visita.');
            return;
        }
        setSedeId(s.id);
        setSeries(x => (x.includes(ns) ? x.filter(y => y !== ns) : [...x, ns]));
    };
    const crearSede = async () => {
        if (!nuevaSede?.nombre?.trim() || !nuevaSede?.direccion?.trim()) { toast.error('Completá el lugar y la dirección'); return; }
        try {
            await guardarUbicacionManual(nuevaSede.direccion.trim(), puntoSede);
            const r = await api.post('/sedes', { clienteId: cliente.id, nombreSede: nuevaSede.nombre.trim(), direccion: nuevaSede.direccion.trim() });
            setSedes(ss => [...ss, { ...r.data, equipos: [] }]);
            setSedeId(r.data.id); setAbierta(r.data.id); setSeries([]); setNuevaSede(null);
            toast.success('Dirección agregada');
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo crear la dirección'); }
    };
    const altaEquipo = async (s) => {
        const ns = window.prompt(`N° de serie del equipo nuevo en ${s.nombreSede}`);
        if (!ns || !ns.trim()) return;
        const serie = ns.trim().toUpperCase();
        try {
            const r = await api.post('/equipos', { numeroSerie: serie, sedeId: s.id });
            setSedes(ss => ss.map(x => (x.id === s.id ? { ...x, equipos: [...(x.equipos || []), { id: r.data?.id, numeroSerie: serie }] } : x)));
            if (!sedeId || sedeId === s.id) { setSedeId(s.id); setSeries(x => [...x, serie]); }
            toast.success(`Equipo ${serie} dado de alta`);
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo dar de alta'); }
    };


    // ── Guardar ──────────────────────────────────────────────────────────────
    const nombreCliente = nuevo ? nuevo.nombre : cliente?.nombre;
    const tecElegido = tecnicos.find(t => t.id === hueco?.tecnicoId);
    const puede2 = (!conTarifa || series.length > 0) && (motivo !== 'Otro' || otroTexto.trim().length >= 3);
    // Título como el de los pedidos de empresa ("Pierde agua · Mauro"): qué hay que hacer y para quién.
    // Antes decía solo el motivo y en las listas se veía "Otro" sin saber de quién ni qué.
    const queHacer = motivo === 'Otro' ? (otroTexto.trim().slice(0, 60) || 'Otro') : motivo;

    const guardar = async () => {
        if (!hueco) { toast.error('Tocá un hueco libre en la agenda'); return; }
        setGuardando(true);
        const t = toast.loading('Guardando…');
        try {
            let cli = cliente;
            if (nuevo) {
                const dir = nuevo.direccion.trim();
                const m = dir.match(/^(.*?)\s+(\d+\S*)\s*$/);
                await guardarUbicacionManual([dir, nuevo.localidad.trim()].filter(Boolean).join(', '), puntoNuevo);
                const r = await api.post('/clientes', {
                    clienteTipo: 'PARTICULAR', condicionIva: 'CONSUMIDOR_FINAL',
                    nombre: nuevo.nombre.trim(), telefono: nuevo.telefono.trim() || null,
                    calle: m ? m[1] : dir, numero: m ? m[2] : 'S/N',
                    localidad: nuevo.localidad.trim() || 'Sin localidad', provincia: 'Buenos Aires',
                    direccion: [dir, nuevo.localidad.trim()].filter(Boolean).join(', '),
                });
                cli = r.data;
            }
            const lugar = sede?.nombreSede && sede.nombreSede !== cli?.nombre ? ` · ${sede.nombreSede}` : '';
            await api.post('/ordenes', {
                tecnicoId: hueco.tecnicoId,
                titulo: `${queHacer} · ${cli?.nombre || 'Cliente'}${series.length ? ` · ${series.length} equipo${series.length !== 1 ? 's' : ''}` : ''}${lugar}`,
                descripcion: [nota.trim(), series.length ? `Equipos: ${series.join(', ')}` : null].filter(Boolean).join('\n') || null,
                clienteId: cli?.id || null,
                clienteNombre: cli?.nombre || '',
                clienteTelefono: cli?.telefono || '',
                direccion: sede?.direccion || cli?.direccion || '',
                prioridad: 'NORMAL',
                fechaProgramada: fecha,
                horaEstimada: hora || hueco.franja,
                montoEstimado: !conTarifa && Number(String(monto).replace(/\D/g, '')) > 0 ? Number(String(monto).replace(/\D/g, '')) : null,
                formaPago: conTarifa ? null : pago,
                presupuestoId: null,
                equiposSerie: series.length ? series.join(',') : null,
                sinConfirmar: tentativa,
            });
            toast.success('Visita agendada', { id: t });
            onGuardado && onGuardado();
        } catch (err) {
            toast.error(err?.response?.data?.mensaje || 'No se pudo guardar', { id: t });
        } finally { setGuardando(false); }
    };

    const titulos = { 1: '¿A quién vas?', 2: '¿Qué hay que hacer?', 3: '¿Quién y cuándo?' };

    return (
        <div className="fixed inset-0 z-[2000] bg-black/60 flex md:items-center md:justify-center md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6">
            <div className="w-full h-full md:h-[calc(var(--vh,1vh)*88)] md:max-w-lg md:rounded-3xl bg-page flex flex-col overflow-hidden">
                {/* Encabezado con progreso */}
                <div className="px-4 pt-4 pb-3 shrink-0">
                    <div className="flex items-center gap-3">
                        <button type="button" onClick={() => (paso === 1 ? onCancelar() : setPaso(p => p - 1))} aria-label={paso === 1 ? 'Cerrar' : 'Atrás'}
                            className="w-10 h-10 rounded-xl bg-chip text-muted flex items-center justify-center active:scale-95">
                            {paso === 1 ? <LuX size={18} /> : <LuChevronLeft size={20} />}
                        </button>
                        <div className="flex-1 min-w-0">
                            <p className="text-label font-black text-muted uppercase tracking-widest">Nueva visita · paso {paso} de 3</p>
                            <h2 className="text-title font-black text-ink leading-tight">{titulos[paso]}</h2>
                        </div>
                    </div>
                    <div className="flex gap-1.5 mt-3">
                        {[1, 2, 3].map(i => <span key={i} className={`flex-1 h-1 rounded-full ${i <= paso ? 'bg-brand-red' : 'bg-chip'}`} />)}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
                    {/* ═══ Paso 1 ═══ */}
                    {paso === 1 && !nuevo && (<>
                        <form onSubmit={e => { e.preventDefault(); if (resultados.length === 1) elegirCliente(resultados[0]); else buscarSerie(); }} className="relative">
                            <LuSearch size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
                            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Cliente, dirección o N° de serie" className={`${INPUT} pl-10`} />
                        </form>
                        {q.trim().length >= 3 && (
                            <button type="button" onClick={buscarSerie} disabled={buscandoNS}
                                className="w-full h-11 rounded-xl border border-dashed border-black/15 dark:border-white/15 text-caption font-bold text-secondary active:scale-[0.99]">
                                {buscandoNS ? 'Buscando…' : `Buscar el equipo con N° de serie "${q.trim().toUpperCase()}"`}
                            </button>
                        )}
                        {!q.trim() && recientes.length > 0 && <p className="pt-1 text-label font-black uppercase tracking-widest text-muted">Recientes</p>}
                        {(q.trim().length >= 2 ? resultados : recientes).map(c => (
                            <button key={c.id} type="button" onClick={() => elegirCliente(c)}
                                className="w-full flex items-center gap-3 px-3.5 py-3 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] text-left active:scale-[0.99]">
                                <span className="w-10 h-10 rounded-full bg-chip text-ink flex items-center justify-center text-label font-black shrink-0">{iniciales(c.nombre)}</span>
                                <span className="flex-1 min-w-0">
                                    <span className="flex items-center gap-1.5 text-body font-black text-ink truncate">
                                        <span className="truncate">{c.nombre}</span>
                                        {c.tieneTarifa && <span className="shrink-0 px-1.5 py-0.5 rounded-md bg-[color:var(--etapa-cobrar-bg)] text-[color:var(--etapa-cobrar-tx)] text-label font-black">Tarifa mensual</span>}
                                    </span>
                                    <span className="block text-caption text-muted truncate">{c.direccion || c.telefono || ''}</span>
                                </span>
                                <LuChevronRight size={18} className="text-muted shrink-0" />
                            </button>
                        ))}
                        {q.trim().length >= 2 && resultados.length === 0 && <p className="text-caption text-muted text-center py-2">Ningún cliente con ese nombre</p>}
                        <button type="button" onClick={() => setNuevo({ nombre: q.trim(), telefono: '', direccion: '', localidad: '' })}
                            className="w-full flex items-center gap-3 px-3.5 py-3 rounded-2xl border-2 border-dashed border-black/15 dark:border-white/15 text-left active:scale-[0.99]">
                            <span className="w-10 h-10 rounded-full bg-[#E8422F]/15 text-brand-red flex items-center justify-center shrink-0"><LuPlus size={20} /></span>
                            <span><span className="block text-body font-black text-ink">Cliente nuevo</span><span className="block text-caption text-muted">Nombre, teléfono y dirección · queda guardado</span></span>
                        </button>
                    </>)}
                    {paso === 1 && nuevo && (
                        <div className="space-y-2.5">
                            <input className={INPUT} placeholder="Nombre *" value={nuevo.nombre} onChange={e => setNuevo(n => ({ ...n, nombre: e.target.value }))} />
                            <input className={INPUT} placeholder="Teléfono" inputMode="tel" value={nuevo.telefono} onChange={e => setNuevo(n => ({ ...n, telefono: e.target.value }))} />
                            <input className={INPUT} placeholder="Calle y número *" value={nuevo.direccion} onChange={e => setNuevo(n => ({ ...n, direccion: e.target.value }))} />
                            <input className={INPUT} placeholder="Localidad" value={nuevo.localidad} onChange={e => setNuevo(n => ({ ...n, localidad: e.target.value }))} />
                            <UbicacionEnMapa direccion={[nuevo.direccion.trim(), nuevo.localidad.trim()].filter(Boolean).join(', ')} onCambio={setPuntoNuevo}
                                onDireccion={d => setNuevo(n => ({ ...n, direccion: [d.calle, d.numero].filter(Boolean).join(' ') || n.direccion, localidad: d.localidad || n.localidad }))} />
                            <button type="button" onClick={() => setNuevo(null)} className="text-caption font-bold text-muted underline">Buscar uno ya cargado</button>
                        </div>
                    )}

                    {/* ═══ Paso 2 ═══ */}
                    {paso === 2 && (<>
                        {/* 1-nov-2026: cada cosa con su rótulo (antes el nombre del cliente quedaba suelto arriba de los botones) */}
                        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-card">
                            <span className="text-label font-black uppercase tracking-widest text-muted">Cliente</span>
                            <span className="flex-1 min-w-0 truncate text-body font-black text-ink">{nombreCliente}</span>
                            {conTarifa && <span className="px-1.5 py-0.5 rounded-md bg-[color:var(--etapa-cobrar-bg)] text-[color:var(--etapa-cobrar-tx)] text-label font-black">Tarifa mensual</span>}
                        </div>
                        <p className="pt-1 text-label font-black uppercase tracking-widest text-muted">Trabajo</p>
                        <div className="flex flex-wrap gap-1.5">
                            {MOTIVOS.map(m => <button key={m} type="button" onClick={() => setMotivo(m)} className={chipCls(motivo === m)}>{m}</button>)}
                        </div>
                        {motivo === 'Otro' && (
                            <input className={INPUT} autoFocus maxLength={60} value={otroTexto} onChange={e => setOtroTexto(e.target.value)}
                                placeholder="¿Qué hay que hacer? (ej. Cambiar canilla)" />
                        )}

                        {/* Sin direcciones cargadas: la del cliente, a la vista (antes no se mostraba ninguna) */}
                        {(nuevo || (cliente && sedes.length === 0)) && (
                            <>
                                <p className="pt-2 text-label font-black uppercase tracking-widest text-muted">Dirección</p>
                                <p className="flex items-center gap-1.5 text-body text-ink"><LuMapPin size={14} className="shrink-0 text-muted" />
                                    {(nuevo ? [nuevo.direccion, nuevo.localidad].filter(Boolean).join(', ') : cliente?.direccion) || <span className="text-muted">Sin dirección: agregá una</span>}</p>
                            </>
                        )}

                        {cliente && sedes.length > 0 && (<>
                            <p className="pt-2 text-label font-black uppercase tracking-widest text-muted">
                                {conTarifa ? 'Equipos · todos de un mismo lugar' : 'Dirección'}
                            </p>
                            {sedes.length > 3 && (
                                <div className="relative">
                                    <LuSearch size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
                                    <input value={buscaEq} onChange={e => setBuscaEq(e.target.value)} placeholder={conTarifa ? 'N° de serie o dirección' : 'Buscar dirección'} className={`${INPUT} pl-10`} />
                                </div>
                            )}
                            {sedesFiltradas.slice(0, 30).map(s => {
                                const elegida = sedeId === s.id;
                                const nSel = elegida ? series.length : 0;
                                const open = conTarifa && abierta === s.id;
                                return (
                                    <div key={s.id} className={`rounded-2xl bg-card border-2 ${elegida ? 'border-brand-red/60' : 'border-transparent'}`}>
                                        <button type="button" onClick={() => (conTarifa ? setAbierta(a => (a === s.id ? null : s.id)) : setSedeId(s.id))}
                                            className="w-full flex items-center gap-3 px-3.5 py-3 text-left">
                                            {conTarifa ? <LuMapPin size={16} className="text-muted shrink-0" /> : <Check on={elegida} />}
                                            <span className="flex-1 min-w-0">
                                                <span className="block text-body font-black text-ink truncate">{s.nombreSede}</span>
                                                <span className="block text-caption text-muted truncate">{s.direccion || '—'}</span>
                                            </span>
                                            {conTarifa && <span className={`text-label font-black shrink-0 ${nSel ? 'text-brand-red' : 'text-muted'}`}>{nSel ? `${nSel} de ` : ''}{(s.equipos || []).length} eq</span>}
                                        </button>
                                        {open && (
                                            <div className="px-3.5 pb-2">
                                                {(s.equipos || []).map(e => (
                                                    <button key={e.id || e.numeroSerie} type="button" onClick={() => toggleEquipo(s, e.numeroSerie)}
                                                        className="w-full flex items-center gap-3 py-2.5 border-t border-black/[0.05] dark:border-white/[0.05] text-left">
                                                        <Check on={elegida && series.includes(e.numeroSerie)} />
                                                        <span className="text-body font-black text-ink">{e.numeroSerie}</span>
                                                        <span className="text-caption text-muted truncate">{e.ubicacion || ''}</span>
                                                    </button>
                                                ))}
                                                <button type="button" onClick={() => altaEquipo(s)} className="w-full py-2.5 border-t border-black/[0.05] dark:border-white/[0.05] text-left text-caption font-bold text-secondary">
                                                    + Equipo nuevo en este lugar
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {sedesFiltradas.length > 30 && <p className="text-caption text-muted text-center">Hay {sedesFiltradas.length - 30} más: buscá por N/S o dirección</p>}
                        </>)}

                        {cliente && (nuevaSede ? (
                            <div className="p-3 rounded-2xl bg-card space-y-2">
                                <input className={INPUT} placeholder="Nombre del lugar (ej: Gimnasio Núñez)" value={nuevaSede.nombre} onChange={e => setNuevaSede(s => ({ ...s, nombre: e.target.value }))} />
                                <input className={INPUT} placeholder="Calle, número y localidad" value={nuevaSede.direccion} onChange={e => setNuevaSede(s => ({ ...s, direccion: e.target.value }))} />
                                <UbicacionEnMapa direccion={nuevaSede.direccion} onCambio={setPuntoSede} />
                                <div className="flex gap-2">
                                    <button type="button" onClick={crearSede} className="h-11 px-4 rounded-xl bg-ink text-page text-label font-black">Agregar dirección</button>
                                    <button type="button" onClick={() => setNuevaSede(null)} className="h-11 px-3 rounded-xl text-label font-bold text-muted">Cancelar</button>
                                </div>
                            </div>
                        ) : (
                            <button type="button" onClick={() => setNuevaSede({ nombre: '', direccion: '' })} className="text-caption font-bold text-secondary inline-flex items-center gap-1 py-1 mr-5"><LuPlus size={14} /> Nueva dirección</button>
                        ))}

                        {!conTarifa && (
                            <div className="pt-2 space-y-1.5">
                                <p className="text-label font-black uppercase tracking-widest text-muted">Cobra en el lugar <span className="normal-case tracking-normal font-bold">(opcional)</span></p>
                                <div className="flex gap-2">
                                    <input className={`${INPUT} flex-1 min-w-0`} inputMode="numeric" placeholder="$ monto" value={monto} onChange={e => setMonto(e.target.value)} />
                                    {['EFECTIVO', 'TRANSFERENCIA'].map(f => (
                                        <button key={f} type="button" onClick={() => setPago(f)} className={chipCls(pago === f)}>{f === 'EFECTIVO' ? 'Efectivo' : 'Transferencia'}</button>
                                    ))}
                                </div>
                                <p className="text-caption text-muted">Si lo sabés, el técnico ve cuánto cobrar. Si no, lo arregla en el lugar.</p>
                            </div>
                        )}

                        {verNota ? (
                            <textarea className="w-full px-3.5 py-3 rounded-xl bg-chip text-ink text-body outline-none resize-none" rows={3} autoFocus
                                value={nota} onChange={e => setNota(e.target.value)} placeholder="Nota para el técnico" />
                        ) : (
                            <button type="button" onClick={() => setVerNota(true)} className="text-caption font-bold text-secondary inline-flex items-center gap-1 py-1"><LuPlus size={14} /> Nota para el técnico</button>
                        )}
                    </>)}

                    {/* ═══ Paso 3: elegí el hueco ═══ */}
                    {paso === 3 && (<>
                        <AgendaHuecos tecnicos={tecnicos} ordenes={ordenes} fecha={fecha} onFecha={setFecha}
                            hueco={hueco} onHueco={setHueco} direccion={sede?.direccion} />

                        {hueco && (
                            <div className="p-3.5 rounded-2xl bg-card space-y-2.5">
                                <p className="text-label font-black uppercase tracking-widest text-muted">Resumen</p>
                                <p className="text-body text-ink leading-relaxed">
                                    {queHacer}{series.length ? <> · <b>{series.length} equipo{series.length !== 1 ? 's' : ''}</b> ({series.join(', ')})</> : null}<br />
                                    {nombreCliente}{sede ? ` · ${sede.nombreSede}` : ''}<br />
                                    <span className="inline-flex items-center gap-1 align-middle"><AvatarTecnico nombre={tecElegido?.nombre} size={18} /><b>{tecElegido?.nombre?.split(' ')[0]}</b></span> · {new Date(fecha + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric' })} · {horaTexto(hora || hueco.franja)}
                                </p>
                                {!conTarifa && Number(String(monto).replace(/\D/g, '')) > 0 && (
                                    <p className="text-caption font-bold text-ink">Cobra ${Number(String(monto).replace(/\D/g, '')).toLocaleString('es-AR')} · {pago === 'EFECTIVO' ? 'efectivo' : 'transferencia'}</p>
                                )}
                                {verHora || hora ? <HoraRango value={hora} onChange={setHora} /> : (
                                    <button type="button" onClick={() => setVerHora(true)} className="text-caption font-bold text-secondary inline-flex items-center gap-1"><LuPlus size={13} /> Hora exacta o rango (opcional)</button>
                                )}
                                <AvisoHoraPasada fecha={fecha} hora={hora} franja={hueco.franja}
                                    onTarde={(fr) => { setHueco({ ...hueco, franja: fr }); setHora(''); }}
                                    onManana={(d) => { setFecha(d); setHora(''); }} />
                                {conTarifa && <p className="text-caption font-bold text-[color:var(--etapa-cobrar-tx)]">Sin precio · va al cierre mensual</p>}
                                <SelectorConfirmacion tentativa={tentativa} onChange={setTentativa} />
                            </div>
                        )}
                    </>)}
                </div>

                {/* Pie con el botón del paso */}
                <div className="shrink-0 px-4 pt-3 pb-5 border-t border-black/[0.06] dark:border-white/[0.06] bg-panel">
                    {paso === 1 && nuevo && (
                        <button type="button" onClick={() => {
                            if (!nuevo.nombre.trim() || !nuevo.direccion.trim()) { toast.error('Completá nombre y dirección'); return; }
                            setCliente(null); setSedes([]); setSedeId(null); setSeries([]); setPaso(2);
                        }} className="w-full h-12 rounded-xl bg-[#C9341F] text-white font-black text-body active:scale-95">Siguiente</button>
                    )}
                    {paso === 1 && !nuevo && <p className="text-center text-caption text-muted py-3">Elegí un cliente para seguir</p>}
                    {paso === 2 && (<>
                        {conTarifa && <p className="text-caption text-muted mb-2">{series.length ? `${series.length} equipo${series.length !== 1 ? 's' : ''} · ${sede?.nombreSede || ''}` : 'Elegí al menos un equipo'}</p>}
                        {motivo === 'Otro' && otroTexto.trim().length < 3 && <p className="text-caption text-muted mb-2">Escribí qué hay que hacer</p>}
                        <button type="button" disabled={!puede2} onClick={() => setPaso(3)} className="w-full h-12 rounded-xl bg-[#C9341F] text-white font-black text-body active:scale-95 disabled:opacity-40">Siguiente</button>
                    </>)}
                    {paso === 3 && (
                        <button type="button" disabled={!hueco || guardando} onClick={guardar} className="w-full h-12 rounded-xl bg-[#C9341F] text-white font-black text-body active:scale-95 disabled:opacity-40">
                            {hueco ? 'Agendar visita' : 'Tocá un hueco en la agenda'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
